import { Box, Paper, keyframes, useMediaQuery } from "@mui/material";
import { purple } from '@mui/material/colors';
import React, { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import ColorService, { PUZZLE_GRID_COLORS } from "../../../services/ColorService";
import {
    WORD_REVEAL_ILLUMINATE_MS,
    WORD_REVEAL_LETTER_STAGGER_MS,
    wordRevealLetterPopKeyframes,
} from "../common/components/WordRevealFx";

const CORRECT_PLACE_BORDER = "#F9A825";
const correctCellBorderPulse = keyframes`
    0% { box-shadow: inset 0 0 0 0 rgba(249, 168, 37, 0); }
    28% { box-shadow: inset 0 0 0 2px ${CORRECT_PLACE_BORDER}, 0 0 12px rgba(249, 168, 37, 0.8); }
    100% { box-shadow: inset 0 0 0 2px ${CORRECT_PLACE_BORDER}, 0 0 0 rgba(249, 168, 37, 0); }
`;

const CrossGrid = forwardRef(({
    grid = [],
    onClickedWords,
    gridSize = 12,
    isFullscreen = false,
    isBoldFont = false,
    filledCoordinates = [],
    hintRevealCells = [],
    highlightCells = [],
    hiddenMessageCells = [],
    /** When true, hidden-message cells use found-word green (e.g. landing carousel demo). */
    hiddenMessageUseFoundStyle = false,
    hideAllWords = false,
    /** Typed letters while answers hidden — keys `"row,col"` */
    cellDraftLetters = {},
    wordsFound = [],
    /** Admin tuning: fixed px size for clue numbers in cell corners */
    clueNumberFontPx,
    /** Admin tuning: fixed px size for letters in cells */
    cellFontPx,
    /** Map `"row,col"` → stagger index for word-reveal letter pop */
    celebrateCellIndexByKey = null,
}, ref) => {

    const isMobile = useMediaQuery('(max-width: 768px)');
    const [clickedCells, setClickedCells] = useState([]);
    const [foundWordCells, setFoundWordCells] = useState([]);

    // Call parent immediately so selection and word cells react to user click without delay
    const handleCellClick = (row, col) => {
        const newCells = [{ row, col }];
        setClickedCells(newCells);
        if (onClickedWords) onClickedWords('', newCells);
    };

    const isCellFound = (row, col) => {
        const isFound = foundWordCells.some(cell => cell.row === row && cell.col === col);
        return isFound;
    };

    const isCellHighlighted = (row, col) => {
        return highlightCells.some(cell => cell.row === row && cell.col === col);
    };

    const isHintRevealCell = (row, col) => {
        return hintRevealCells.some(cell => cell.row === row && cell.col === col);
    };

    const isCellHiddenMessage = (row, col) => {
        return hiddenMessageCells.some(cell => cell.row === row && cell.col === col);
    };

    // Get the highlight color for a cell based on its character index
    const getHighlightColor = (row, col) => {
        const cell = highlightCells.find(cell => cell.row === row && cell.col === col);
        if (!cell || cell.charIndex === undefined) {
            return PUZZLE_GRID_COLORS.ORANGE; // Default orange if no charIndex
        }

        // Different shades of orange based on character position (0, 1, 2, ...)
        const colors = ColorService.getHighlightColors();

        const charIndex = cell.charIndex || 0;
        return colors[Math.min(charIndex, colors.length - 1)];
    };

    const clearClickedCells = () => {
        setClickedCells([]);
        setFoundWordCells([]);
        if (onClickedWords) onClickedWords('', []);
    };

    const clearSelection = () => {
        setClickedCells([]);
        if (onClickedWords) onClickedWords('', []);
    };

    const markClickedCellsAsFound = (specificCells = null) => {
        const cellsToMove = specificCells ? [...specificCells] : [...clickedCells];
        setClickedCells([]);
        setTimeout(() => {
            setFoundWordCells(prev => [...prev, ...cellsToMove]);
        }, 0);
        if (onClickedWords) onClickedWords('', []);
    };

    // Expose functions to parent component
    useImperativeHandle(ref, () => ({
        clearFoundWords: clearClickedCells, // For clear all found words
        clearSelection: clearSelection, // For clear only current selection
        markClickedCellsAsFound: markClickedCellsAsFound // For when word is found
    }));

    // Get the first character position of a word
    const getFirstPosition = (coord) => {
        if (!coord.positions || coord.positions.length === 0) return null;

        // Exclude hidden message words
        if (coord.direction === "scattered") {
            return null;
        }

        let firstPos;

        if (coord.reversed) {
            // Reversed word: first character of word text is at the last position in positions array
            firstPos = coord.positions[coord.positions.length - 1];
        } else {
            // Normal word: get first position based on direction
            if (coord.direction === "vertical") {
                firstPos = coord.positions.reduce((min, pos) => pos.row < min.row ? pos : min);
            } else if (coord.direction === "horizontal") {
                firstPos = coord.positions.reduce((min, pos) => pos.col < min.col ? pos : min);
            } else {
                // For diagonal directions, use the first position in the array
                firstPos = coord.positions[0];
            }
        }

        return firstPos;
    };

    const isFirstCharacter = (row, col, filledCoordinates) => {
        if (!filledCoordinates) return false;

        return filledCoordinates.some(coord => {
            const firstPos = getFirstPosition(coord);
            return firstPos && firstPos.row === row && firstPos.col === col;
        });
    };

    // Calculate word numbers: assign numbers to words based on their starting positions
    // Words are ordered left-to-right, top-to-bottom
    // Words that share the same starting cell get the same number
    const wordNumbers = useMemo(() => {
        if (!filledCoordinates || filledCoordinates.length === 0) {
            return new Map(); // Map of "row,col" -> number
        }

        // Collect all unique starting positions
        const startingPositions = new Map(); // Map of "row,col" -> {row, col}

        filledCoordinates.forEach(coord => {
            // Skip hidden message words
            if (coord.direction === "scattered") {
                return;
            }

            const firstPos = getFirstPosition(coord);
            if (!firstPos) return;

            const cellKey = `${firstPos.row},${firstPos.col}`;

            // Store the position if not already stored
            if (!startingPositions.has(cellKey)) {
                startingPositions.set(cellKey, { row: firstPos.row, col: firstPos.col });
            }
        });

        // Sort positions: top-to-bottom (by row), then left-to-right (by column)
        const sortedPositions = Array.from(startingPositions.values()).sort((a, b) => {
            if (a.row !== b.row) {
                return a.row - b.row; // Top to bottom
            }
            return a.col - b.col; // Left to right
        });

        // Assign numbers to sorted positions
        const cellToNumber = new Map();
        sortedPositions.forEach((pos, index) => {
            const cellKey = `${pos.row},${pos.col}`;
            cellToNumber.set(cellKey, index + 1); // Start from 1
        });

        return cellToNumber;
    }, [filledCoordinates]);

    // Get word number for a specific cell
    const getWordNumber = (row, col) => {
        const cellKey = `${row},${col}`;
        return wordNumbers.get(cellKey) || null;
    };

    return (
        <Paper
            elevation={3}
            sx={{
                p: 1,
                backgroundColor: PUZZLE_GRID_COLORS.BACKGROUND_LIGHT,
                userSelect: 'none',
                display: 'grid',
                gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${gridSize}, minmax(0, 1fr))`,
                gap: 0.25,
                borderRadius: 1,
                border: `2px solid ${PUZZLE_GRID_COLORS.GRID_OUTER_BORDER}`,
                width: '100%',
                maxWidth: '100%',
                height: '100%',
                maxHeight: '100%',
                aspectRatio: '1',
                minWidth: 0,
                minHeight: 0,
                transition: 'all 0.3s ease',
                margin: '0 auto',
                flexShrink: 1,
                boxSizing: 'border-box',
                overflow: 'hidden'
            }}
        >
            {grid.map((row, rowIndex) =>
                row.map((cell, colIndex) => (
                    <Box
                        key={`${rowIndex}-${colIndex}`}
                        data-puzzle-cell={`${rowIndex},${colIndex}`}
                        sx={{
                            width: '100%',
                            height: '100%',
                            minHeight: 0,
                            minWidth: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            backgroundColor: (() => {
                                const isFound = isCellFound(rowIndex, colIndex);
                                const isHighlighted = isCellHighlighted(rowIndex, colIndex);
                                const isHiddenMessage = isCellHiddenMessage(rowIndex, colIndex);
                                const isEmpty = !cell || cell.trim() === '' || cell === ' ';

                                let color = PUZZLE_GRID_COLORS.WHITE; // default

                                // Hidden message cells get highest priority
                                if (isHiddenMessage) {
                                    color = hiddenMessageUseFoundStyle
                                        ? PUZZLE_GRID_COLORS.GREEN_FOUND
                                        : PUZZLE_GRID_COLORS.HIDDEN_MESSAGE_BG;
                                } else if (isHighlighted) {
                                    color = getHighlightColor(rowIndex, colIndex);  // Different color for each character position
                                } else if (isFound) {
                                    color = PUZZLE_GRID_COLORS.GREEN_FOUND;
                                } else if (isHintRevealCell(rowIndex, colIndex)) {
                                    color = purple[100]; // Light purple for hint-revealed letters
                                } else if (isEmpty) {
                                    color = '#bdbdbd'; // Gray for empty cells
                                }

                                return color;
                            })(),
                            color: (() => {
                                const isHiddenMessage = isCellHiddenMessage(rowIndex, colIndex);

                                if (isHiddenMessage && !hiddenMessageUseFoundStyle) {
                                    return PUZZLE_GRID_COLORS.HIDDEN_MESSAGE_TEXT;
                                }

                                return PUZZLE_GRID_COLORS.TEXT_DARK;
                            })(),
                            fontFamily: "'Open Sans', sans-serif",
                            fontSize: (() => {
                                if (Number.isFinite(cellFontPx)) {
                                    return `${cellFontPx}px`;
                                }
                                if (isMobile) {
                                    return 'clamp(0.4rem, 1.72vw, 0.68rem)';
                                }
                                if (isFullscreen) {
                                    return 'clamp(0.68rem, 1.72vw, 1.34rem)';
                                }
                                return 'clamp(0.56rem, 1.02vw, 0.82rem)';
                            })(),
                            fontWeight: (() => {
                                const isHiddenMessage = isCellHiddenMessage(rowIndex, colIndex);
                                // Hidden message cells always bold for emphasis
                                if (isHiddenMessage) {
                                    return 700;
                                }
                                return isBoldFont ? 700 : 400;
                            })(),
                            cursor: 'pointer',
                            border: (() => {
                                const draftChar = cellDraftLetters[`${rowIndex},${colIndex}`];
                                const solution = cell != null ? String(cell).trim().toUpperCase() : "";
                                const isCorrectDraft =
                                    hideAllWords &&
                                    draftChar &&
                                    solution &&
                                    String(draftChar).toUpperCase() === solution &&
                                    !isCellFound(rowIndex, colIndex);
                                if (isCorrectDraft) {
                                    return `2px solid ${CORRECT_PLACE_BORDER}`;
                                }
                                return `1px solid ${PUZZLE_GRID_COLORS.BORDER_LIGHT}`;
                            })(),
                            animation:
                                celebrateCellIndexByKey?.[`${rowIndex},${colIndex}`] != null
                                    ? `${correctCellBorderPulse} ${WORD_REVEAL_ILLUMINATE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
                                    : "none",
                            animationDelay:
                                celebrateCellIndexByKey?.[`${rowIndex},${colIndex}`] != null
                                    ? `${celebrateCellIndexByKey[`${rowIndex},${colIndex}`] * WORD_REVEAL_LETTER_STAGGER_MS}ms`
                                    : undefined,
                            borderRadius: 0.25,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: (() => {
                                    const isHiddenMessage = isCellHiddenMessage(rowIndex, colIndex);
                                    const isHighlighted = isCellHighlighted(rowIndex, colIndex);
                                    const isFound = isCellFound(rowIndex, colIndex);

                                    if (isHiddenMessage) {
                                        return hiddenMessageUseFoundStyle
                                            ? PUZZLE_GRID_COLORS.GREEN_FOUND_HOVER
                                            : PUZZLE_GRID_COLORS.HIDDEN_MESSAGE_HOVER;
                                    }

                                    if (isHighlighted) {
                                        const baseColor = getHighlightColor(rowIndex, colIndex);
                                        // Darken the color on hover
                                        const hoverColors = ColorService.getHighlightHoverColors();
                                        return hoverColors[baseColor] || PUZZLE_GRID_COLORS.ORANGE_HOVER_1;
                                    } else if (isFound) {
                                        return PUZZLE_GRID_COLORS.GREEN_FOUND_HOVER;
                                    }

                                    return PUZZLE_GRID_COLORS.HOVER_BACKGROUND;
                                })()
                            }
                        }}
                        onClick={() => handleCellClick(rowIndex, colIndex)}
                    >
                        {(() => {
                            const draftChar = cellDraftLetters[`${rowIndex},${colIndex}`];
                            const showSolutionLetter =
                                isCellFound(rowIndex, colIndex) ||
                                isHintRevealCell(rowIndex, colIndex);
                            let displayChar = null;
                            if (showSolutionLetter) {
                                displayChar = cell;
                            } else if (hideAllWords && draftChar) {
                                displayChar = draftChar;
                            } else if (!hideAllWords) {
                                displayChar = cell;
                            }
                            const clueNum = getWordNumber(rowIndex, colIndex);

                            return (
                                <>
                                    {clueNum != null ? (
                                        <Box
                                            component="span"
                                            sx={{
                                                position: "absolute",
                                                top: 2,
                                                left: 3,
                                                lineHeight: 1,
                                                fontSize: (() => {
                                                    if (Number.isFinite(clueNumberFontPx)) {
                                                        return `${clueNumberFontPx}px`;
                                                    }
                                                    if (isMobile) {
                                                        return "clamp(9px, 3vw, 14px)";
                                                    }
                                                    if (isFullscreen) {
                                                        return "clamp(12px, 1.2vw, 22px)";
                                                    }
                                                    return "clamp(12px, 1vw, 18px)";
                                                })(),
                                                fontWeight: isMobile ? 800 : 700,
                                                fontFamily: "'Open Sans', sans-serif",
                                                color: "#2E8B57",
                                                zIndex: 1,
                                                userSelect: "none",
                                                pointerEvents: "none",
                                            }}
                                        >
                                            {clueNum}
                                        </Box>
                                    ) : null}
                                    {displayChar != null && displayChar !== "" ? (
                                        <Box
                                            component="span"
                                            sx={{
                                                position: "relative",
                                                zIndex: 2,
                                                lineHeight: 1,
                                                display: "inline-block",
                                                willChange:
                                                    celebrateCellIndexByKey?.[
                                                        `${rowIndex},${colIndex}`
                                                    ] != null
                                                        ? "transform"
                                                        : undefined,
                                                ...(celebrateCellIndexByKey?.[
                                                    `${rowIndex},${colIndex}`
                                                ] != null
                                                    ? {
                                                          animation: `${wordRevealLetterPopKeyframes} ${WORD_REVEAL_ILLUMINATE_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both`,
                                                          animationDelay: `${
                                                              celebrateCellIndexByKey[
                                                                  `${rowIndex},${colIndex}`
                                                              ] * WORD_REVEAL_LETTER_STAGGER_MS
                                                          }ms`,
                                                      }
                                                    : {}),
                                            }}
                                        >
                                            {displayChar}
                                        </Box>
                                    ) : null}
                                </>
                            );
                        })()}
                    </Box>
                ))
            )}
        </Paper>
    );
});

CrossGrid.displayName = 'CrossGrid';

export default CrossGrid;

